import React from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const ManufacturingUseCase = () => {
  const keyScenarios = [
    "Multi-robot coordination with human oversight on production floor",
    "Predictive maintenance orchestration across legacy PLCs and modern sensors",
    "Remote factory diagnostics with secure vendor access and audit trails"
  ];

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#242423' }}>
      <div className="max-w-7xl mx-auto">
        <div 
          className="rounded-xl p-8 md:p-12"
          style={{ backgroundColor: '#2E2E2C', border: '1px solid #3A3A38' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column - Text Content */}
            <div className="flex flex-col justify-center">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider mb-6 w-fit"
                style={{ backgroundColor: 'rgba(79, 179, 184, 0.2)', border: '1px solid rgba(79, 179, 184, 0.4)', color: '#4FB3B8' }}
              >
                Manufacturing
              </div>

              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: '#E8EDDF' }}>
                Robotics & Manufacturing
              </h2>

              <p className="text-base leading-relaxed mb-8" style={{ color: '#9CA3AF' }}>
                Orchestrating multi-agent factory environments where robotic cells, quality inspection systems, and maintenance teams coordinate in real-time. Remote operations, adaptive workflows, and embedded security across distributed production lines.
              </p>

              <div className="mb-8">
                <h3 className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: '#9CA3AF' }}>
                  Key Scenarios
                </h3>
                <ul className="space-y-4">
                  {keyScenarios.map((scenario, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div 
                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                        style={{ backgroundColor: '#4FB3B8' }}
                      ></div>
                      <span className="text-base" style={{ color: '#E8EDDF' }}>{scenario}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link 
                to="/use-cases/manufacturing"
                className="inline-flex items-center gap-2 text-base font-medium transition-colors duration-250 w-fit"
                style={{ color: '#4FB3B8' }}
              >
                View Manufacturing Use Case
                <Icon name="ArrowRight" size={16} style={{ color: '#4FB3B8' }} />
              </Link>
            </div>

            {/* Right Column - Diagram */}
            <div className="flex items-center justify-center">
              <div 
                className="w-full rounded-lg p-6"
                style={{ backgroundColor: '#242423', border: '1px solid #3A3A38' }}
              >
                <svg width="100%" height="340" viewBox="0 0 480 340" className="max-w-full">
                  
                  {/* Top Row - Robotic Arms, Robotic Inspection, Automated Machinery */}
                  <rect x="40" y="20" width="100" height="50" rx="6" fill="#242423" stroke="#3A3A38" strokeWidth="2" />
                  <text x="90" y="42" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">ROBOTIC</text>
                  <text x="90" y="54" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">ARMS</text>
                  
                  <rect x="190" y="20" width="100" height="50" rx="6" fill="#242423" stroke="#3A3A38" strokeWidth="2" />
                  <text x="240" y="42" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">ROBOTIC</text>
                  <text x="240" y="54" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">INSPECTION</text>
                  
                  <rect x="340" y="20" width="100" height="50" rx="6" fill="#242423" stroke="#3A3A38" strokeWidth="2" />
                  <text x="390" y="42" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">AUTOMATED</text>
                  <text x="390" y="54" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">MACHINERY</text>
                  
                  {/* Connection lines from top row to orchestration */}
                  <line x1="90" y1="70" x2="90" y2="100" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="90" y1="100" x2="240" y2="100" stroke="#4FB3B8" strokeWidth="2" />
                  
                  <line x1="240" y1="70" x2="240" y2="130" stroke="#4FB3B8" strokeWidth="2" />
                  
                  <line x1="390" y1="70" x2="390" y2="100" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="390" y1="100" x2="240" y2="100" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Operator Circle - Left */}
                  <circle cx="50" cy="170" r="35" fill="#242423" stroke="#F5CB5C" strokeWidth="2" />
                  <text x="50" y="175" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">OPERATOR</text>
                  
                  {/* Line from Operator to Orchestration */}
                  <line x1="85" y1="170" x2="130" y2="170" stroke="#F5CB5C" strokeWidth="2" />
                  
                  {/* Orchestration Box - Center */}
                  <rect x="130" y="130" width="220" height="80" rx="8" fill="#242423" stroke="#4FB3B8" strokeWidth="2" />
                  <text x="240" y="165" textAnchor="middle" fill="#E8EDDF" fontSize="13" fontWeight="700">ORCHESTRATION</text>
                  <text x="240" y="185" textAnchor="middle" fill="#9CA3AF" fontSize="10">Coordination Layer</text>
                  
                  {/* Connection lines from orchestration to bottom row */}
                  <line x1="240" y1="210" x2="240" y2="240" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="90" y1="240" x2="390" y2="240" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Vertical lines to bottom nodes */}
                  <line x1="90" y1="240" x2="90" y2="270" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="240" y1="240" x2="240" y2="270" stroke="#4FB3B8" strokeWidth="2" />
                  <line x1="390" y1="240" x2="390" y2="270" stroke="#4FB3B8" strokeWidth="2" />
                  
                  {/* Bottom Row - Legacy PLCs (hexagon), Sensors (hexagon), Maintenance Systems (rect) */}
                  {/* Legacy PLCs - Hexagon with coral */}
                  <polygon 
                    points="90,270 50,290 50,320 90,340 130,320 130,290" 
                    fill="#242423" 
                    stroke="#FF6B6B" 
                    strokeWidth="2"
                  />
                  <text x="90" y="302" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">LEGACY</text>
                  <text x="90" y="314" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">PLCs</text>
                  
                  {/* Sensors - Hexagon with teal */}
                  <polygon 
                    points="240,270 200,290 200,320 240,340 280,320 280,290" 
                    fill="#242423" 
                    stroke="#4FB3B8" 
                    strokeWidth="2"
                  />
                  <text x="240" y="310" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">SENSORS</text>
                  
                  {/* Maintenance Systems - Rectangle with gray */}
                  <rect x="330" y="275" width="120" height="55" rx="6" fill="#242423" stroke="#3A3A38" strokeWidth="2" />
                  <text x="390" y="300" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">MAINTENANCE</text>
                  <text x="390" y="312" textAnchor="middle" fill="#E8EDDF" fontSize="9" fontWeight="600">SYSTEMS</text>
                  
                </svg>
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </section>
  );
};

export default ManufacturingUseCase;